import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {NavigationEnd, Router} from '@angular/router';
import {
  NavigationRouteInheritanceService,
  RouteMenuItem,
} from '@gatewee/services/navigation-route-inheritance.service';
import {Subscription} from 'rxjs';
import {NavigationMenuStateService} from '../services/navigation-menu-state.service';

export interface BreadcrumbItem {
  label: string;
  path?: string;
}

export type BreadcrumbMode = 'manual' | 'dynamic' | 'append';

@Component({
  selector: 'gatewee-content-page',
  templateUrl: './gatewee-content-page.component.html',
  styleUrls: ['./gatewee-content-page.component.css'],
  standalone: false,
})
export class GateweeContentPageComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() breadcrumb: BreadcrumbItem[] = [];
  @Input() title = '';
  @Input() menuItems: RouteMenuItem[] = [];
  @Input() breadcrumbRoute?: string;
  @Input() breadcrumbMode: BreadcrumbMode = 'dynamic';

  resolvedBreadcrumb: BreadcrumbItem[] = [];
  private routerEventsSub?: Subscription;
  private menuItemsSub?: Subscription;

  constructor(
    private router: Router,
    private navigationRouteInheritanceService: NavigationRouteInheritanceService,
    private navigationMenuStateService: NavigationMenuStateService,
  ) {}

  ngOnInit(): void {
    this.menuItemsSub = this.navigationMenuStateService.menuItems$.subscribe(
      menuItems => {
        if (this.menuItems?.length) {
          return;
        }

        this.menuItems = menuItems;
        this.updateResolvedBreadcrumb();
      },
    );

    this.updateResolvedBreadcrumb();

    this.routerEventsSub = this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.updateResolvedBreadcrumb();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['breadcrumb'] ||
      changes['menuItems'] ||
      changes['breadcrumbRoute'] ||
      changes['breadcrumbMode']
    ) {
      this.updateResolvedBreadcrumb();
    }
  }

  ngOnDestroy(): void {
    this.routerEventsSub?.unsubscribe();
    this.menuItemsSub?.unsubscribe();
  }

  private updateResolvedBreadcrumb(): void {
    const manualBreadcrumb = [...(this.breadcrumb ?? [])];
    const sourceMenuItems = this.menuItems?.length
      ? this.menuItems
      : (this.navigationMenuStateService.menuItems as RouteMenuItem[]);

    if (this.breadcrumbMode === 'manual') {
      this.resolvedBreadcrumb = manualBreadcrumb;
      return;
    }

    if (!sourceMenuItems?.length) {
      this.resolvedBreadcrumb = manualBreadcrumb;
      return;
    }

    const routeToResolve = this.breadcrumbRoute || this.router.url;

    const dynamicBreadcrumb = this.navigationRouteInheritanceService
      .getActiveNodePath(routeToResolve, sourceMenuItems)
      .map(item => ({
        label: item.title,
        path: item.route,
      }));

    this.resolvedBreadcrumb =
      this.breadcrumbMode === 'append'
        ? [...dynamicBreadcrumb, ...manualBreadcrumb]
        : dynamicBreadcrumb;
  }
}
